import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ShieldOff, Plus, Eye } from 'lucide-react';
import { accountService } from '../api/accountRepo';
import { Account } from '../types';
import { canManageStaffDB } from '../../../lib/rbac';
import { useStore } from '../../../lib/store';

interface AccountSummaryProps {
  staffId: string;
}

const AccountSummary: React.FC<AccountSummaryProps> = ({ staffId }) => {
  const { user } = useStore();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    checkPermissions();
    loadAccount();
  }, [staffId, user]);

  const checkPermissions = async () => {
    if (!user?.id) {
      setCanManageAccounts(false);
      setCheckingPermissions(false);
      return;
    }

    try {
      const canManage = await canManageStaffDB(user.id);
      setCanManageAccounts(canManage);
    } catch (error) {
      console.error('Error checking permissions:', error);
      setCanManageAccounts(false);
    } finally {
      setCheckingPermissions(false);
    }
  };

  const loadAccount = async () => {
    try {
      setLoading(true);
      const accounts = await accountService.list();
      const staffAccount = accounts.find(acc => acc.staffId === staffId);
      setAccount(staffAccount || null);
    } catch (error) {
      console.error('Error loading account:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: "text-green-600",
      invited: "text-yellow-600",
      disabled: "text-red-600"
    };
    return colors[status as keyof typeof colors] || "text-gray-600";
  };

  if (loading) {
    return <div className="text-sm text-gray-500">Loading account status...</div>;
  }

  if (!account) {
    return (
      <div className="text-center">
        <div className="text-gray-500 font-medium">No Account</div>
        <div className="text-sm text-gray-400 mb-3">Staff member has no login account</div>
        {canManageAccounts && (
          <Link
            to="/staff/accounts/new"
            className="inline-flex items-center px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Create Account
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className={`flex items-center ${getStatusColor(account.status)}`}>
          {account.status === 'active' ? (
            <Shield className="w-5 h-5 mr-2" />
          ) : (
            <ShieldOff className="w-5 h-5 mr-2" />
          )}
          <span className="font-medium capitalize">{account.status}</span>
        </div>
        {canManageAccounts && (
          <Link
            to={`/staff/accounts/${account.id}`}
            className="text-blue-600 hover:text-blue-800"
            title="View Account"
          >
            <Eye className="w-4 h-4" />
          </Link>
        )}
      </div>
      
      <div className="text-sm space-y-1">
        <div>
          <span className="text-gray-500">Email:</span>
          <span className="ml-2 text-gray-900">{account.email}</span>
        </div>
        <div>
          <span className="text-gray-500">Last Login:</span>
          <span className="ml-2 text-gray-900">
            {account.lastLoginAt ? new Date(account.lastLoginAt).toLocaleDateString() : 'Never'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccountSummary;
